import { useMemo, useState } from 'react';

import { btn, field, table, text } from '../ui';
import { currentHolders, describeOverrideRange, groupOverrides } from '../schedule.mjs';

const fmtDay = (date) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const fmtRange = (start, end) => (start === end ? fmtDay(start) : `${fmtDay(start)} to ${fmtDay(end)}`);

const holderLabel = (span) =>
  `${span.person || 'nobody'} (${fmtRange(span.startDate, span.endDate)})`;

export default function SwapPanel({
  zoneNames,
  schedule,
  people,
  overrides,
  onApply,
  onRemove,
  scheduleRange,
}) {
  const [kind, setKind] = useState('SLR');
  const [zone, setZone] = useState(zoneNames[0] || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [person, setPerson] = useState('');

  // Fall back to the first zone if the selected one was removed under us.
  const activeZone = zoneNames.includes(zone) ? zone : zoneNames[0] || '';
  const target = kind === 'CLR' ? 'CLR' : activeZone;
  const from = startDate;
  const to = endDate && endDate > startDate ? endDate : startDate;
  const backwards = Boolean(endDate && startDate && endDate < startDate);

  const holders = useMemo(
    () => currentHolders(schedule, target, from, to),
    [schedule, target, from, to]
  );
  const groups = useMemo(() => groupOverrides(overrides), [overrides]);

  const outOfRange = Boolean(from && !backwards && holders.length === 0);
  const ready = Boolean(target && from && person && !backwards && !outOfRange);
  const span = ready ? describeOverrideRange({ target, startDate: from, endDate: to }) : null;

  // The one person the swap would replace, if the range has a single holder.
  const soleHolder = holders.length === 1 ? holders[0].person : null;

  const reset = () => {
    setStartDate('');
    setEndDate('');
    setPerson('');
  };

  const apply = () => {
    onApply({ target, startDate: from, endDate: to, person });
    reset();
  };

  return (
    <div className="border-t pt-6">
      <h2 className="text-lg font-semibold mb-1">Swap SLR &amp; CLR</h2>
      <p className={`${text.hint} mb-4`}>
        Replace whoever is on for a date range. The rotation itself is unchanged, and Google Calendar
        is not touched until you run Sync to Calendar.
      </p>

      <div className="flex gap-1 mb-4">
        {['SLR', 'CLR'].map((k) => (
          <button
            key={k}
            onClick={() => {
              setKind(k);
              setPerson('');
            }}
            className={`px-4 py-1.5 text-sm rounded-t border-b-2 ${
              kind === k
                ? 'border-blue-500 font-medium text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {k === 'SLR' ? 'SLR (shift)' : 'CLR (week)'}
          </button>
        ))}
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex gap-4 flex-wrap items-end mb-3">
          {kind === 'SLR' && (
            <label className="text-sm">
              <span className={field.labelSm}>Zone</span>
              <select
                value={activeZone}
                onChange={(e) => setZone(e.target.value)}
                className={field.inputSm}
              >
                {zoneNames.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm">
            <span className={field.labelSm}>From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={field.inputSm}
            />
          </label>
          <label className="text-sm">
            <span className={field.labelSm}>To (optional)</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className={field.inputSm}
            />
          </label>
        </div>

        {backwards && (
          <p className="text-sm text-red-600 mb-3">The end date is before the start date.</p>
        )}

        {outOfRange && (
          <p className="text-sm p-2 rounded bg-red-50 border border-red-200 mb-3">
            That range is outside the schedule currently shown
            {scheduleRange ? ` (${scheduleRange.first} to ${scheduleRange.last})` : ''}. Pick an
            earlier <strong>Start</strong> or more <strong>Weeks</strong> on the Schedule tab.
          </p>
        )}

        {holders.length > 0 && (
          <>
            <p className="text-sm mb-3">
              <span className="text-gray-500">Currently on {target}: </span>
              {holders.map(holderLabel).join(', ')}
            </p>

            <div className="flex gap-4 flex-wrap items-end">
              <label className="text-sm">
                <span className={field.labelSm}>Replace with</span>
                <select
                  value={person}
                  onChange={(e) => setPerson(e.target.value)}
                  className={field.inputSm}
                >
                  <option value="">Select person…</option>
                  {people
                    .filter((name) => name !== soleHolder)
                    .map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                </select>
              </label>

              {ready && (
                <button onClick={apply} className={btn.primary}>
                  Replace {soleHolder ? `${soleHolder} ` : ''}with {person} for {span.count}{' '}
                  {span.unit}, {fmtRange(from, to)}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <h3 className="font-medium mt-6 mb-2">Active swaps</h3>
      {groups.length === 0 ? (
        <p className={text.hint}>No swaps.</p>
      ) : (
        <table className="border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className={table.cellHead}>Type</th>
              <th className={table.cellHead}>Dates</th>
              <th className={table.cellHead}>Covered by</th>
              <th className={table.cell}></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.keys[0]}>
                <td className={table.cell}>{group.kind === 'CLR' ? 'CLR' : `SLR ${group.target}`}</td>
                <td className={table.cell}>
                  {group.kind === 'CLR'
                    ? `week of ${fmtRange(group.startDate, group.endDate)}`
                    : fmtRange(group.startDate, group.endDate)}
                </td>
                <td className={table.cell}>{group.person}</td>
                <td className={table.cell}>
                  <button onClick={() => onRemove(group.keys)} className={btn.danger}>remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
